import React from 'react';
import { Box, Typography, Button, ToggleButton, ToggleButtonGroup, Skeleton, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { colors } from '../../themes/typography';
import useRecentSales from '../../hooks/useRecentSales';

const TIME_ZONE = 'Europe/Madrid';

const formatAgo = (date, lang) => {
  const rtf = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' });
  const minutes = Math.round((date.getTime() - Date.now()) / 60000);
  if (Math.abs(minutes) < 60) return rtf.format(minutes, 'minute');
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return rtf.format(hours, 'hour');
  return rtf.format(Math.round(hours / 24), 'day');
};

// 24h in every language and a numeric date for past years, so it fits the narrow date column
const formatDateTime = (date, lang) => new Intl.DateTimeFormat(lang, {
  timeZone: TIME_ZONE,
  ...(date.getFullYear() !== new Date().getFullYear()
    ? { day: 'numeric', month: 'numeric', year: '2-digit' }
    : { day: 'numeric', month: 'short' }),
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23'
}).format(date);

const findSlotForSale = (slots, sale) => {
  const sameProduct = (slots || []).filter(s => Number(s.d_product_id) === Number(sale.productId));
  return sameProduct.find(s => String(s.selection_id) === String(sale.selection)) || sameProduct[0] || null;
};

const RecentSalesSection = ({ storeId, productId, clientId, slots, currentSlot, onJumpToSlot, defaultScope = 'product' }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'es';
  const [scope, setScope] = React.useState(defaultScope);

  const isMachineScope = scope === 'machine';
  const { data, loading, error } = useRecentSales(storeId, isMachineScope ? null : productId, clientId);

  const money = React.useMemo(
    () => new Intl.NumberFormat(lang, { style: 'currency', currency: 'EUR' }),
    [lang]
  );

  const columns = isMachineScope ? '90px minmax(0, 1fr) 40px 76px 66px' : '1fr 52px 72px 72px';
  const headerSx = { fontSize: '10px', fontWeight: 600, color: colors.text.muted, textTransform: 'uppercase' };
  const cellSx = { fontSize: '12px', color: colors.text.secondary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

  const renderRow = (sale, index) => {
    const date = new Date(sale.date);
    const targetSlot = isMachineScope ? findSlotForSale(slots, sale) : null;
    const isCurrentSlot = targetSlot && currentSlot
      && targetSlot.row === currentSlot.row && targetSlot.column === currentSlot.column;
    const canJump = Boolean(targetSlot && !isCurrentSlot && onJumpToSlot);
    const isCurrentProduct = isMachineScope && Number(sale.productId) === Number(productId);
    const jumpLabel = canJump ? t('machines_recentSalesJumpToSlot', { product: sale.productName }) : undefined;

    const row = (
      <Box
        key={`${sale.date}-${index}`}
        role={canJump ? 'button' : undefined}
        tabIndex={canJump ? 0 : undefined}
        aria-label={jumpLabel}
        onClick={canJump ? () => onJumpToSlot(targetSlot) : undefined}
        onKeyDown={canJump ? (e) => { if (e.key === 'Enter') onJumpToSlot(targetSlot); } : undefined}
        sx={{
          display: 'grid',
          gridTemplateColumns: columns,
          gap: 1,
          alignItems: 'center',
          px: 0.75,
          py: 0.5,
          borderRadius: '4px',
          cursor: canJump ? 'pointer' : 'default',
          '&:hover': canJump ? { backgroundColor: colors.background.subtle } : undefined
        }}
      >
        <Typography sx={cellSx}>{formatDateTime(date, lang)}</Typography>
        {isMachineScope && (
          <Typography sx={{ ...cellSx, color: colors.text.primary, fontWeight: isCurrentProduct ? 600 : 400 }}>
            {sale.productName || '—'}
          </Typography>
        )}
        <Typography sx={cellSx}>{sale.selection || '—'}</Typography>
        <Typography sx={{ ...cellSx, textAlign: 'right' }}>
          {sale.total > 0 ? money.format(sale.total) : '—'}
          {sale.quantity > 1 ? ` ×${sale.quantity}` : ''}
        </Typography>
        <Typography sx={{ ...cellSx, textAlign: 'right' }}>
          {sale.isCash === null ? '—' : t(sale.isCash ? 'machines_recentSalesCash' : 'machines_recentSalesCard')}
        </Typography>
      </Box>
    );

    return canJump ? <Tooltip key={`${sale.date}-${index}`} title={jumpLabel} placement="left" arrow>{row}</Tooltip> : row;
  };

  return (
    <Box sx={{ px: 2, py: 1.5 }} data-testid="recent-sales-section">
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 1 }}>
        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: colors.text.primary }}>
          {t('machines_recentSales')}
        </Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={scope}
          onChange={(e, value) => { if (value) setScope(value); }}
          aria-label={t('machines_recentSalesScope')}
        >
          <ToggleButton value="product" sx={{ fontSize: '11px', py: 0.25, px: 1, textTransform: 'none' }}>
            {t('machines_recentSalesThisProduct')}
          </ToggleButton>
          <ToggleButton value="machine" sx={{ fontSize: '11px', py: 0.25, px: 1, textTransform: 'none' }}>
            {t('machines_recentSalesWholeMachine')}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {loading && (
        <Box>
          {[0, 1, 2, 3, 4].map(i => <Skeleton key={i} variant="text" height={24} />)}
        </Box>
      )}

      {!loading && error && (
        <Typography sx={{ fontSize: '12px', color: colors.text.muted, textAlign: 'center', py: 2 }}>
          {t('machines_recentSalesError')}
        </Typography>
      )}

      {!loading && !error && data?.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Typography sx={{ fontSize: '12px', color: colors.text.muted }}>
            {t('machines_recentSalesEmpty')}
          </Typography>
          {!isMachineScope && (
            <Button size="small" onClick={() => setScope('machine')} sx={{ mt: 0.5, fontSize: '12px', textTransform: 'none' }}>
              {t('machines_recentSalesSeeWholeMachine')}
            </Button>
          )}
        </Box>
      )}

      {!loading && !error && data?.length > 0 && (
        <>
          <Typography sx={{ fontSize: '12px', color: colors.text.secondary, mb: 1 }}>
            {t('machines_recentSalesLastSale', {
              ago: formatAgo(new Date(data[0].date), lang),
              date: formatDateTime(new Date(data[0].date), lang)
            })}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: columns, gap: 1, px: 0.75, pb: 0.5, borderBottom: `1px solid ${colors.border.light}` }}>
            <Typography sx={headerSx}>{t('machines_recentSalesDate')}</Typography>
            {isMachineScope && <Typography sx={headerSx}>{t('product')}</Typography>}
            <Typography sx={headerSx}>{t('machines_recentSalesLane')}</Typography>
            <Typography sx={{ ...headerSx, textAlign: 'right' }}>{t('price')}</Typography>
            <Typography sx={{ ...headerSx, textAlign: 'right' }}>{t('machines_recentSalesPayment')}</Typography>
          </Box>
          <Box sx={{ mt: 0.5 }}>
            {data.map(renderRow)}
          </Box>
        </>
      )}
    </Box>
  );
};

export default RecentSalesSection;
